import React, { useEffect, useRef, useCallback } from 'react';
import { useChatStore } from '../../store/chatStore';
import { ChatMessageList } from './ChatMessageList';
import { ChatInput } from './ChatInput';

/**
 * ChatPanel - Slide-out chat container component
 *
 * Requirements:
 * - 1.1: Slide-out panel triggered by persistent chat icon
 * - 1.2: Smooth slide-in animation from right edge
 * - 1.5: Welcome message and suggested questions on first open
 * - 1.6: 420px max width desktop, full-width < 768px
 * - 1.7: WCAG 2.1 AA: keyboard nav, focus trap, ARIA labels, escape to close
 * - 7.2: Session persistence across page navigations
 * - 7.3: New Chat button archives current session
 */
export const ChatPanel: React.FC = () => {
  const { isOpen, togglePanel, messages, archiveAndStartNewChat, restoreSession } = useChatStore();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Store the element that triggered the panel open so we can return focus
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      // Move focus into the panel after animation starts
      const timer = setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      // Return focus to the triggering element
      previousFocusRef.current?.focus();
    }
  }, [isOpen]);

  // Restore session from sessionStorage when panel opens
  // Requirement 7.2: Load session history when panel reopens within same browser session
  useEffect(() => {
    if (isOpen) {
      restoreSession();
    }
  }, [isOpen, restoreSession]);

  // Escape key closes the panel
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        togglePanel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, togglePanel]);

  // Focus trap: Tab/Shift+Tab stay within the panel when open
  const handleFocusTrap = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Tab' || !isOpen || !panelRef.current) return;

      const focusableElements = panelRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift+Tab: wrap to last element
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab: wrap to first element
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    },
    [isOpen]
  );

  return (
    <>
      {/* Floating Chat Widget - anchored bottom-right, above the launcher
          button. Always floats regardless of breakpoint (no sm:-gated
          full-screen mode): width/height are clamped with calc()/min() so
          it shrinks to fit on narrow viewports instead of ever switching
          to a full-screen side-panel layout - a breakpoint-based switch
          previously misfired on wide desktop windows that had browser
          zoom/OS display scaling active, since that shrinks the effective
          CSS viewport width used by media queries below 640px even when
          the physical window is wide. */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="AI Chat Assistant"
        onKeyDown={handleFocusTrap}
        className={`fixed bottom-6 right-6 z-50 flex h-[min(640px,calc(100vh-3rem))] w-[min(400px,calc(100vw-1.5rem))] flex-col rounded-2xl bg-card shadow-2xl ring-1 ring-black/5 transition-all duration-200 ease-out dark:bg-card dark:ring-white/10 ${
          isOpen
            ? 'translate-y-0 opacity-100'
            : 'pointer-events-none translate-y-2 opacity-0'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between rounded-t-2xl border-b border-border px-4 py-3 dark:border-border">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-primary to-sky-700 text-white">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8-1.17 0-2.29-.196-3.316-.552L3 21l1.395-4.185C3.512 15.42 3 13.766 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </span>
            <h2 className="text-base font-semibold text-foreground">
              AI Assistant
            </h2>
          </div>
          <div className="flex items-center gap-1">
            {/* New Chat button - Requirement 7.3 */}
            <button
              onClick={archiveAndStartNewChat}
              aria-label="Start new chat"
              title="New Chat"
              className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground/80 focus:outline-none focus:ring-2 focus:ring-ring dark:text-muted-foreground/70 dark:hover:bg-secondary dark:hover:text-foreground"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </button>
            {/* Close button */}
            <button
              ref={closeButtonRef}
              onClick={togglePanel}
              aria-label="Close chat panel"
              className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground/80 focus:outline-none focus:ring-2 focus:ring-ring dark:text-muted-foreground/70 dark:hover:bg-secondary dark:hover:text-foreground"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Message List */}
        <div className="flex-1 overflow-y-auto p-4">
          <ChatMessageList messages={messages} />
        </div>

        {/* Input Footer */}
        <div className="border-t border-border p-4 dark:border-border">
          <ChatInput />
        </div>
      </div>
    </>
  );
};
